'use client';

import { APP_STYLE_CONFIG } from '@/constants/config';
import { Button, ButtonProps } from '@/components/Buttons';
import { cn } from '@/utils';
import { COLORS } from '@/design-tokens';
import { hasAnyPassingAttempt } from '@/domains/scoring';
import { Icon } from '@/components/Icons';
import { ISimulationScore, TSimulationStatus, TSimulationType } from '@/types';
import { PassFailTag, RoleplayTag, Tag } from '@/components/Tags';
import { RoleplayCardContentContainer } from './subcomponents/RoleplayCardContentContainer';
import { RoleplayCardHistory } from './subcomponents/RoleplayCardHistory';
import { RoleplayCardIntro } from './subcomponents/RoleplayCardIntro';
import { roleplayCardStyles } from './RoleplayCard.styles';
import { TAG_LABELS } from '@/constants';
import { TextContent } from '@/components/Text';
import { useAnimatedHeightTransition } from '@/hooks';
import React, { useMemo, useState, useTransition } from 'react';

export interface RoleplayCardProps {
  /**
   * The objective title for the roleplay scenario.
   */
  title: string;

  /**
   * The description text explaining the roleplay scenario.
   */
  description?: string;

  /**
   * The avatar's name for the roleplay scenario.
   */
  avatarName?: string;

  /**
   * The avatar image source for the doctor.
   */
  avatarSrc?: string;

  /**
   * Alternative text for the avatar image.
   */
  avatarAlt?: string;

  /**
   * EM Code associated with the roleplay scenario.
   */
  emCode?: string;

  /**
   * Previous roleplay session scores and dates.
   */
  history?: ISimulationScore[];

  /**
   * Handler for when the roleplay button is clicked.
   */
  onRoleplayClick?: () => void;

  /**
   * Handler for when a previous session's "Share with manager" is clicked.
   */
  onShareWithManager?: (scoreId: string) => void;

  /**
   * Handler for when a previous session's "View scorecard" is clicked.
   */
  onViewScoreCard?: (scoreId: string) => void;

  /**
   * The status of the simulation.
   */
  status?: TSimulationStatus;

  /**
   * Array of action buttons for the simulation.
   */
  actions?: ButtonProps[];

  /**
   * Whether to show the best score from history.
   */
  showBestScore?: boolean;

  /**
   * Determines whether to show the skill level based on the
   * latest score or the highest score in the history.
   */
  showSkillLevelAs?: 'latest' | 'highest';

  /**
   * Whether to show the icon left of the title.
   */
  showTitleIcon?: boolean;

  /**
   * Type of simulation.
   */
  type?: TSimulationType;

  /**
   * Index of the card in a list (used to auto-expand the first card).
   */
  index?: number;

  /**
   * Identifying number for the simulation.
   */
  id?: number;

  /**
   * Additional class names for the card container.
   */
  className?: string;

  /**
   * Whether this roleplay scenario is new (used to show "New!" badge).
   */
  isNew?: boolean;

  /**
   * User ID for sharing functionality.
   */
  userId?: string;

  /**
   * Loading state for the card, used to show skeletons instead of content.
   */
  isLoading?: boolean;

  /**
   * Whether to animate the roleplay card content when it appears.
   * Controlled by global style config.
   */
  useAnimatedRoleplayCards?: boolean;
}

/**
 * RoleplayCard displays either a detailed roleplay scenario with description,
 * doctor avatar, and history OR a simple simulation card with status and actions.
 */
export const RoleplayCard: React.FC<RoleplayCardProps> = ({
  actions = [],
  avatarAlt,
  avatarName,
  avatarSrc,
  className,
  description,
  emCode,
  history = [],
  id,
  index,
  isLoading = false,
  isNew,
  onRoleplayClick,
  onShareWithManager,
  onViewScoreCard,
  showBestScore = false,
  showSkillLevelAs = 'highest',
  showTitleIcon = false,
  status,
  title,
  type,
  useAnimatedRoleplayCards = APP_STYLE_CONFIG.useAnimatedRoleplayCards,
  userId,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPending, startTransition] = useTransition();
  const { contentRef, height, handleTransitionEnd, isAnimating, prepareForTransition } =
    useAnimatedHeightTransition({
      dependencies: [actions.length, description, history.length, isExpanded, isLoading],
      getCurrentHeight: (element) => {
        return isExpanded ? element?.getBoundingClientRect().height : 0;
      },
      getNextHeight: (element) => {
        return isExpanded ? element?.getBoundingClientRect().height : 0;
      },
    });

  /**
   * Determine if this is a simple card (has status/actions) vs detailed card (has description)
   */
  const isSimpleCard = !description && Boolean(status || actions.length > 0);

  /**
   * Calculate the best score from history sessions
   */
  const bestScore = useMemo(() => {
    if (history.length === 0) return null;
    if (history.length === 1) return null;

    const validScores = history
      .map((score) => score.score)
      .filter((score): score is number => typeof score === 'number');

    if (validScores.length === 0) return null;
    return Math.max(...validScores);
  }, [history]);

  /**
   * Determine whether to show the roleplay tag based on the presence of history and simulation type.
   * For 'role-play' types, we show the tag if there's any history.
   * For 'practice' and 'certification' types, we show the tag only if there's history with scores.
   */
  const showRoleplayTag = useMemo(() => {
    if (isLoading) return false; // Don't show tag if we're still loading data
    const hasHistory = history?.length !== 0;
    const simScores = history.filter((s) => s.simulationId === id?.toString());
    const showTag = hasHistory && simScores.length ? true : false;

    if (type === 'role-play') return true; // Always show tag for role-play types
    return showTag;
  }, [history, id, isLoading, type]);

  /**
   * If a practice or certification simulation has at least one passing score, we want to show
   * a pass/fail tag on the card header.
   */
  const getTopPassFailTag = useMemo(() => {
    const simScores = history.filter((s) => s.simulationId === id?.toString());
    const hasAtLeastOnePass = hasAnyPassingAttempt(simScores, type);

    // For practice and certification types, show pass/fail based on whether there's at
    // least one passing score. For role-play types, we don't show pass/fail tags.
    if (type === 'practice' || type === 'certification') {
      return hasAtLeastOnePass ? 'pass' : 'fail';
    }

    return null;
  }, [history, id, type]);

  /**
   * Combine all class names for the card container
   */
  const cardClassNames = cn(roleplayCardStyles.base, className, {
    'bg-neutral-gray-100': status === 'locked' && type !== 'role-play',
  });

  /**
   * Wrap an action callback in a transition-aware click handler.
   */
  const getActionClickHandler = (action: ButtonProps) => {
    return (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!action.onClick) return;

      startTransition(() => {
        action.onClick?.(e);
      });
    };
  };

  /**
   * Roleplay status icon component
   */
  const RoleplayStatusIcon = () => (
    <>
      {showTitleIcon && status && (
        <div className={roleplayCardStyles.playIcon}>
          {status === 'available' && <Icon name="PlayCircle" size={24} />}
          {status === 'locked' && <Icon name="Lock" size={24} />}
          {status === 'in-progress' && <Icon name="PlayPause" size={24} />}
          {status === 'completed' && (
            <Icon name="CheckCircle" size={24} color={COLORS.system.success} />
          )}
        </div>
      )}
    </>
  );

  // If this is a simple card, render the simple layout
  if (isSimpleCard) {
    return (
      <section className={cardClassNames} data-status={status}>
        <div
          className={cn(
            roleplayCardStyles.header,
            'flex-col items-start md:flex-row md:items-center',
          )}
        >
          <div className={roleplayCardStyles.headerLeft}>
            <RoleplayStatusIcon />
            {isNew && <Tag label="New!" variant="new" />}
            <TextContent
              type="headline"
              level={2}
              label={title}
              isLoading={isLoading}
              loadingClassName="h-[1.5rem] xl:h-[1.75rem]"
              className={cn(roleplayCardStyles.title, '!max-w-none', {
                'text-neutral-gray-700': status === 'locked',
              })}
            />

            {showRoleplayTag && (
              <>
                {type === 'role-play' && (
                  <RoleplayTag
                    showSkillLevelAs={showSkillLevelAs}
                    simulationType={type}
                    history={history}
                  />
                )}

                {getTopPassFailTag && (
                  <PassFailTag
                    simulationType={type}
                    showIcon={false}
                    textForPass={type === 'practice' ? TAG_LABELS.READY_TO_CERTIFY : undefined}
                    variant={getTopPassFailTag}
                  />
                )}
              </>
            )}
          </div>

          <div className={roleplayCardStyles.headerRight}>
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'secondary'}
                size="small"
                disabled={isLoading || action.disabled}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cardClassNames} data-status={status}>
      {/* Header Section */}
      <div className={roleplayCardStyles.header}>
        {/* Left side - Icon and title */}
        <div className={roleplayCardStyles.headerLeft}>
          <RoleplayStatusIcon />
          {isNew && <Tag label="New!" variant="new" />}
          <TextContent
            type="headline"
            level={2}
            label={title}
            isLoading={isLoading}
            className={roleplayCardStyles.title}
            loadingClassName="h-[1.5rem] xl:h-[1.75rem]"
          />

          {showRoleplayTag && (
            <>
              {type === 'role-play' && (
                <RoleplayTag
                  showSkillLevelAs={showSkillLevelAs}
                  simulationType={type}
                  history={history}
                />
              )}

              {getTopPassFailTag && (
                <PassFailTag
                  simulationType={type}
                  showIcon={false}
                  textForPass={type === 'practice' ? TAG_LABELS.READY_TO_CERTIFY : undefined}
                  variant={getTopPassFailTag}
                />
              )}
            </>
          )}

          {showBestScore && bestScore && !isLoading && (
            <div className={roleplayCardStyles.bestScoreContainer}>
              <div className={roleplayCardStyles.scoreTag}>
                <div className={roleplayCardStyles.scoreText}>Best score {bestScore}%</div>
              </div>
            </div>
          )}
        </div>

        {/* Right side - Actions */}
        {description && (
          <div className={roleplayCardStyles.headerRight}>
            <Button
              disabled={isLoading || status === 'locked'}
              onClick={() => {
                prepareForTransition();
                setIsExpanded(!isExpanded);
              }}
              size="small"
              variant="tertiary"
            >
              <span className={roleplayCardStyles.viewDetailsButton}>
                <span className={roleplayCardStyles.viewDetailsText}>
                  {isExpanded ? 'Hide' : 'View'} details
                </span>
                <Icon name={isExpanded ? 'CaretUp' : 'CaretDown'} size={12} weight="fill" />
              </span>
            </Button>

            {actions?.length !== 0 && (
              <div className="hidden items-center justify-start gap-4 md:flex lg:hidden xl:flex">
                {actions.map((action, index) => (
                  <Button
                    {...action}
                    key={index}
                    size="small"
                    variant={action.variant || 'secondary'}
                    disabled={isLoading || action.disabled}
                    isTransitioning={isPending}
                    onClick={getActionClickHandler(action)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expanded Content */}
      <div
        aria-hidden={!isExpanded}
        className={cn(
          'w-full overflow-hidden transition-[height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
          {
            'pointer-events-none': !isExpanded,
          },
        )}
        data-testid="roleplay-card-expanded-content-wrapper"
        onTransitionEnd={handleTransitionEnd}
        style={
          typeof height === 'number'
            ? { height: `${height}px` }
            : isExpanded
              ? undefined
              : { height: '0px' }
        }
      >
        <div
          className={cn('transition-opacity duration-200 ease-out motion-reduce:transition-none', {
            'opacity-95': isAnimating,
          })}
          ref={contentRef}
        >
          {isExpanded && (
            <div className={roleplayCardStyles.body}>
              {actions?.length !== 0 && (
                <div className="overfloy-x-auto flex w-full items-center justify-end gap-4 md:hidden lg:flex xl:hidden">
                  {actions.map((action, index) => (
                    <Button
                      {...action}
                      key={index}
                      size="small"
                      variant={action.variant || 'secondary'}
                      disabled={isLoading || action.disabled}
                      isTransitioning={isPending}
                      onClick={getActionClickHandler(action)}
                    />
                  ))}
                </div>
              )}

              {/* Scenario Introduction */}
              {description && (
                <RoleplayCardContentContainer animateIn={useAnimatedRoleplayCards}>
                  <RoleplayCardIntro
                    avatarAlt={avatarAlt}
                    avatarName={avatarName}
                    avatarSrc={avatarSrc}
                    description={description}
                    emCode={emCode}
                    isLoading={isLoading}
                  />
                </RoleplayCardContentContainer>
              )}

              {/* History Section */}
              {history.length > 0 && (
                <RoleplayCardContentContainer
                  animateIn={useAnimatedRoleplayCards}
                  animationIndex={1}
                >
                  <RoleplayCardHistory
                    isLoading={isLoading}
                    onShareWithManager={onShareWithManager}
                    onViewScoreCard={onViewScoreCard}
                    scores={history}
                    showSkillLevelAs={showSkillLevelAs}
                    simulationType={type}
                    userId={userId}
                  />
                </RoleplayCardContentContainer>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

RoleplayCard.displayName = 'RoleplayCard';

export default RoleplayCard;
